import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, Upload, FileText, Trash2, IndianRupee } from 'lucide-react';
import { API } from '@/App';

/**
 * Create a single consolidated tour for a customer from multiple invoice
 * PDFs/images. Use-case: operator raises N small part-payment invoices against
 * one tour (e.g. Indian Roadtrip Cruisers) and wants them rolled up into a
 * single tour without polluting the Avg Ticket Size KPI.
 *
 * Flow:
 *   1. Operator picks multiple files.
 *   2. Per-file: enter the invoice amount shown on that invoice.
 *   3. Enter the shared trip fields (destination, dates, invoice_date,
 *      source_type, status). Tour name auto-derives from "{customer} —
 *      {destination} ({Mmm YYYY})" and remains editable.
 *   4. Submit → backend creates 1 tour whose `customer_amount` = sum of the
 *      per-invoice amounts. Each uploaded file is saved against the customer
 *      (no tour_id) with category=Invoice so they stay individually visible
 *      inside the customer's Documents list.
 */
const AddTourFromInvoicesDialog = ({ open, onClose, customer, onCreated }) => {
  const POLLEN_API = `${API}/pollen`;
  const inputRef = useRef(null);
  const [rows, setRows] = useState([]); // {file, amount}
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [sourceType, setSourceType] = useState('Direct');
  const [status, setStatus] = useState('Booked');
  const [tourName, setTourName] = useState('');
  const [busy, setBusy] = useState(false);

  const authFetch = (url, opts = {}) => {
    const h = {};
    const t = localStorage.getItem('session_token');
    if (t) h['Authorization'] = `Bearer ${t}`;
    return fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...h } });
  };

  const handleClose = () => {
    if (busy) return;
    setRows([]);
    setDestination('');
    setStartDate('');
    setEndDate('');
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    setSourceType('Direct');
    setStatus('Booked');
    setTourName('');
    onClose?.();
  };

  const addFiles = (filesList) => {
    const next = Array.from(filesList || []).map(f => ({ file: f, amount: '' }));
    setRows(prev => [...prev, ...next]);
  };

  const removeRow = (idx) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const updateAmount = (idx, val) => {
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, amount: val } : r)));
  };

  const total = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const missingAmount = rows.some(r => !r.amount || isNaN(parseFloat(r.amount)));

  // Auto-suggest a tour name once the operator enters the destination.
  const autoName = () => {
    if (!destination) return '';
    const d = invoiceDate || new Date().toISOString().slice(0, 10);
    let label = d.slice(0, 7);
    try {
      label = new Date(d).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    } catch { /* keep YYYY-MM */ }
    const who = customer?.name || 'Customer';
    return `${who} - ${destination} (${label})`;
  };
  const effectiveName = (tourName || '').trim() || autoName();

  const submit = async () => {
    if (!customer?.id) { toast.error('Customer not loaded'); return; }
    if (rows.length === 0) { toast.error('Add at least one invoice file'); return; }
    if (!destination.trim()) { toast.error('Destination is required'); return; }
    if (!invoiceDate) { toast.error('Invoice date is required'); return; }
    if (missingAmount) { toast.error('Enter the amount for every invoice'); return; }
    if (!effectiveName) { toast.error('Tour name is required'); return; }
    setBusy(true);
    try {
      // 1) Create the consolidated tour.
      const payload = {
        tour_name: effectiveName,
        customer_id: customer.id,
        customer_name: customer.name || '',
        source_type: sourceType,
        destination: destination.trim(),
        start_date: startDate || '',
        end_date: endDate || '',
        invoice_date: invoiceDate,
        customer_amount: total,
        status,
        adults: 1,
        children: 0,
      };
      const res = await authFetch(`${POLLEN_API}/tours`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not create tour');
      }
      const created = await res.json();

      // 2) Upload every invoice file, attached to the CUSTOMER (no tour_id)
      //    with category=Invoice — so they live in the customer's Documents
      //    individually, as operator requested.
      let uploaded = 0;
      let failed = 0;
      for (const row of rows) {
        const fd = new FormData();
        fd.append('file', row.file);
        fd.append('customer_id', customer.id);
        fd.append('category', 'Invoice');
        try {
          const ur = await authFetch(`${POLLEN_API}/files/upload`, { method: 'POST', body: fd });
          if (ur.ok) uploaded += 1; else failed += 1;
        } catch {
          failed += 1;
        }
      }

      toast.success(
        `Tour created · ${uploaded} invoice${uploaded === 1 ? '' : 's'} saved` +
        (failed ? ` · ${failed} failed` : '')
      );
      onCreated?.(created);
      handleClose();
    } catch (e) {
      toast.error(e.message || 'Failed to create tour');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="add-tour-invoices-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add tour · consolidate multiple invoices
          </DialogTitle>
          <DialogDescription>
            Upload every invoice PDF/image for this tour. Enter the amount on each. We&apos;ll create <b>one</b> tour
            whose amount is the sum. Each invoice is saved individually under {customer?.name || 'this customer'}&apos;s Documents.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          {/* File picker */}
          <div>
            <Label className="text-xs">Invoices</Label>
            <div className="mt-1 border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/40 transition-colors">
              <input
                ref={inputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.csv,.docx"
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                data-testid="add-tour-invoices-files"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} data-testid="add-tour-invoices-pick">
                <Upload className="w-3.5 h-3.5 mr-1" /> {rows.length ? 'Add more files' : 'Select invoice files'}
              </Button>
              <p className="text-[11px] text-muted-foreground mt-2">PDF, JPG, PNG, XLSX, CSV, DOCX · pick several at once</p>
            </div>
            {rows.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {rows.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 border rounded-md bg-muted/20" data-testid={`invoice-row-${i}`}>
                    <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="text-xs truncate flex-1" title={r.file.name}>{r.file.name}</span>
                    <div className="relative w-32 shrink-0">
                      <IndianRupee className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="h-7 text-xs pl-6"
                        placeholder="Amount"
                        value={r.amount}
                        onChange={(e) => updateAmount(i, e.target.value)}
                        data-testid={`invoice-amount-${i}`}
                      />
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive shrink-0" onClick={() => removeRow(i)} data-testid={`invoice-remove-${i}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 px-1 text-sm border-t">
                  <span className="text-muted-foreground text-xs">Consolidated amount ({rows.length} invoice{rows.length === 1 ? '' : 's'})</span>
                  <span className="font-semibold tabular-nums" data-testid="invoice-total">₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            )}
          </div>

          {/* Manual tour fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs">Tour Name</Label>
              <Input
                className="h-9"
                placeholder={autoName() || 'e.g. Roadtrip Cruisers - Ladakh (Aug 2025)'}
                value={tourName}
                onChange={(e) => setTourName(e.target.value)}
                data-testid="add-tour-name"
              />
              {!tourName && destination && (
                <p className="text-[11px] text-muted-foreground mt-1">Will save as <span className="font-mono">{autoName()}</span></p>
              )}
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Destination <span className="text-destructive">*</span></Label>
              <Input
                className="h-9"
                placeholder="e.g. Ladakh, Spiti, Goa"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                data-testid="add-tour-destination"
              />
            </div>
            <div>
              <Label className="text-xs">Invoice Date <span className="text-destructive">*</span></Label>
              <Input type="date" className="h-9" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} data-testid="add-tour-invoice-date" />
              <p className="text-[10px] text-muted-foreground mt-0.5">Decides the fiscal-year bucket.</p>
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Select value={sourceType} onValueChange={setSourceType}>
                <SelectTrigger className="h-9" data-testid="add-tour-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Direct', 'OTA', 'Agent', 'Mixed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Travel Start</Label>
              <Input type="date" className="h-9" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="add-tour-start-date" />
            </div>
            <div>
              <Label className="text-xs">Travel End</Label>
              <Input type="date" className="h-9" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="add-tour-end-date" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9" data-testid="add-tour-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Planning', 'Booked', 'Ongoing', 'Completed', 'Cancelled'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={busy} data-testid="add-tour-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || rows.length === 0 || !destination.trim() || missingAmount} data-testid="add-tour-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Create tour {rows.length > 0 ? `· ₹${total.toLocaleString('en-IN')}` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddTourFromInvoicesDialog;
